import { Controller } from "@hotwired/stimulus"

// A choose-one field's answer sheet (paperwork_tasks/_input_fields): a radio
// group of rows, with an optional "a different value" row whose field sits
// under it. Writes the chosen value into a hidden field that posts with the
// form.
//
// The ARIA radio pattern: only the picked row is a tab stop, the arrow keys
// move the pick (and the focus) along the rows, Space or Enter picks the row
// in focus.
export default class extends Controller {
  static targets = ["value", "option", "customInput"]

  choose(event) {
    const row = event.currentTarget
    this.pick(row)
    if (row.dataset.custom === "true" && this.hasCustomInputTarget && event.type !== "keydown") this.customInputTarget.focus()
  }

  key(event) {
    const rows = this.optionTargets
    const index = rows.indexOf(event.currentTarget)
    if (index < 0) return
    let next
    switch (event.key) {
      case "ArrowDown":
      case "ArrowRight":
        next = rows[(index + 1) % rows.length]
        break
      case "ArrowUp":
      case "ArrowLeft":
        next = rows[(index - 1 + rows.length) % rows.length]
        break
      case " ":
      case "Enter":
        event.preventDefault()
        this.pick(event.currentTarget)
        // Enter on "a different value" goes on to its field.
        if (event.key === "Enter" && event.currentTarget.dataset.custom === "true" && this.hasCustomInputTarget) this.customInputTarget.focus()
        return
      default:
        return
    }
    event.preventDefault()
    this.pick(next)
    next.focus()
  }

  pick(row) {
    this.highlight(row)
    if (row.dataset.custom === "true") {
      if (this.hasCustomInputTarget) this.valueTarget.value = this.customInputTarget.value
    } else {
      this.valueTarget.value = row.dataset.value || ""
    }
    this.carryAnswer(row)
  }

  // The form's send carries the picked row's answer: the agent's button it
  // repeats (TaskChoiceHelper), or the note alone for a row without one.
  // A row with neither leaves the send disabled, saying why.
  carryAnswer(row) {
    const field = this.element.querySelector("[data-choice-field]")?.dataset.choiceField
    const sends = [...(this.element.closest("form")?.querySelectorAll("[data-choice-send]") || [])]
    const send = sends.find((candidate) => candidate.dataset.choiceSend === field)
    if (!send) return
    const label = row.dataset.answerLabel || send.dataset.fallbackLabel
    const value = row.dataset.answerValue || send.dataset.fallbackValue
    if (!label || !value) {
      send.disabled = true
      send.title = send.dataset.noAnswerTitle || ""
      return
    }
    send.disabled = false
    send.title = "Send the picked answer"
    send.textContent = label
    send.value = value
    send.dataset.requiresNotes = row.dataset.answerRequiresNotes === "true" ? "true" : "false"
  }

  typeCustom() {
    const customRow = this.customRow()
    if (customRow && !customRow.hasAttribute("data-selected")) this.pick(customRow)
    this.valueTarget.value = this.customInputTarget.value
  }

  focusCustom() {
    const customRow = this.customRow()
    if (customRow) this.pick(customRow)
  }

  customRow() {
    return this.optionTargets.find((row) => row.dataset.custom === "true")
  }

  highlight(row) {
    this.optionTargets.forEach((option) => {
      const on = option === row
      option.toggleAttribute("data-selected", on)
      option.setAttribute("aria-checked", String(on))
      option.tabIndex = on ? 0 : -1
    })
    // The value field is a tab stop only while its row is the pick.
    if (this.hasCustomInputTarget) this.customInputTarget.tabIndex = row.dataset.custom === "true" ? 0 : -1
  }
}
